import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import React, { useCallback, useState } from "react";
import * as Yup from "yup";

import { generateUniqueId } from "utils/uuid";
import useAddJewellery, { diamondType } from "./hooks/useAddJewellery";
import Select from "react-select";

type AddDiamondModelPropsType = {
  show: boolean;
  setShow: (val: boolean) => void;
  diamonds: diamondType[];
  setDiamonds: (val: diamondType[]) => void;
  diamondEventData: diamondType | null;
  setDiamondEventData: (val: diamondType | null) => void;
  isEdit: boolean;
  setIsEdit: (val: boolean) => void;
};

export type diamondSpecType = {
  type: "shape" | "color" | "clarity";
  label: string;
  options: { value: string; label: string }[];
};

const diamondShapes = [
  { value: "", label: "Select Shape" },
  { value: "round", label: "Round" },
  { value: "princess", label: "Princess" },
  { value: "radiant", label: "Radiant" },
  { value: "asscher", label: "Asscher" },
  { value: "cushion", label: "Cushion" },
  { value: "oval", label: "Oval" },
  { value: "emerald", label: "Emerald" },
  { value: "pear", label: "Pear" },
  { value: "marquise", label: "Marquise" },
  { value: "heart", label: "Heart" },
];

const diamondColors = [
  { value: "", label: "Select Color" },
  { value: "def", label: "DEF" },
  { value: "ghi", label: "GHI" },
];

const diamondClarities = [
  { value: "", label: "Select Clarity" },
  { value: "vvs", label: "VVS" },
  { value: "vs", label: "VS" },
];

const diamondSpecs: diamondSpecType[] = [
  { type: "shape", label: "Shape", options: diamondShapes },
  { type: "color", label: "Color", options: diamondColors },
  { type: "clarity", label: "Clarity", options: diamondClarities },
];

const AddDiamondModel = ({
  setShow,
  show,
  diamonds,
  setDiamonds,
  diamondEventData,
  setDiamondEventData,
  isEdit,
  setIsEdit,
}: AddDiamondModelPropsType) => {
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      shape: diamondEventData?.shape || "",
      color: diamondEventData?.color || "",
      clarity: diamondEventData?.clarity || "",
      carat: diamondEventData?.carat || 0,
      pieces: diamondEventData?.pieces || 0,
    },
    validationSchema: Yup.object({
      shape: Yup.string().required("Please Enter Shape"),
      color: Yup.string().required("Please Enter Color"),
      clarity: Yup.string().required("Please Enter Clarity"),
      carat: Yup.number()
        .moreThan(0, "Carat must be bigger than 0")
        .required("Please Enter Carat"),
      pieces: Yup.number()
        .moreThan(0, "Pieces must be bigger than 0")
        .required("Please Enter Pieces"),
    }),

    onSubmit: async (values) => {
      let clonedDiamonds = [...diamonds];

      if (isEdit) {
        const index = diamonds.findIndex(
          (diamond: diamondType) => diamond._id === diamondEventData?._id
        );
        clonedDiamonds[index] = {
          ...clonedDiamonds[index],
          ...values,
        };
      } else {
        clonedDiamonds.push({
          ...values,
          _id: generateUniqueId(),
        });
      }
      setDiamonds(clonedDiamonds);
      toggle();
    },
  });

  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setDiamondEventData(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setShow(true);
      setDiamondEventData(null);
      validation.resetForm();
    }
  }, [show, validation]);

  return (
    <Modal
      show={show}
      onHide={toggle}
      modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600"
    >
      <Modal.Header
        className="flex items-center justify-between p-4 border-b dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
      >
        <Modal.Title className="text-16">
          {!!isEdit ? "Edit Diamond" : "Add Diamond"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <form
          action="#!"
          onSubmit={(e) => {
            e.preventDefault();
            console.log("validation:errors", validation.errors);
            validation.handleSubmit();
            return false;
          }}
          onReset={() => validation.resetForm()}
        >
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            {diamondSpecs.map((diamondSpec: diamondSpecType, index: number) => {
              const selectedOption = diamondSpec.options.find(
                (option) => option.value === validation.values[diamondSpec.type]
              );

              return (
                <div className="xl:col-span-12" key={`jewellery-spec-${index}`}>
                  <label
                    htmlFor={`${diamondSpec.type}-${index}`}
                    className="inline-block mb-2 text-base font-medium"
                  >
                    {diamondSpec.label}
                  </label>
                  <Select
                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    options={diamondSpec.options}
                    // isDisabled={isLoading}
                    value={selectedOption}
                    name={diamondSpec.type}
                    id={`${diamondSpec.type}-${index}`}
                    placeholder={`Select ${diamondSpec.label}`}
                    onChange={(data) => {
                      validation.setFieldValue(
                        diamondSpec.type,
                        data?.value || ""
                      );
                    }}
                  />
                  {validation.touched[`${diamondSpec.type}`] &&
                    validation.errors[`${diamondSpec.type}`] && (
                      <p className="mt-1 text-sm text-red-400">
                        {validation.errors[`${diamondSpec.type}`]}
                      </p>
                    )}
                </div>
              );
            })}

            <div className="xl:col-span-12">
              <label
                htmlFor="carat"
                className="inline-block mb-2 text-base font-medium"
              >
                Carat
              </label>
              <input
                type="text"
                id="carat"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="0.5 Gm"
                name="carat"
                onChange={(e) => {
                  if (!isNaN(Number(e.target.value.trim()))) {
                    validation.handleChange(e);
                  }
                }}
                value={validation.values.carat || ""}
              />
              {validation.touched.carat && validation.errors.carat ? (
                <p className="text-red-400">{validation.errors.carat}</p>
              ) : null}
            </div>
            <div className="xl:col-span-12">
              <label
                htmlFor="pieces"
                className="inline-block mb-2 text-base font-medium"
              >
                Pieces
              </label>
              <input
                type="number"
                id="pieces"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="1"
                name="pieces"
                onChange={validation.handleChange}
                value={validation.values.pieces || ""}
              />
              {validation.touched.pieces && validation.errors.pieces ? (
                <p className="text-red-400">{validation.errors.pieces}</p>
              ) : null}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="reset"
              className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-600 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"
              onClick={toggle}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            >
              {!!isEdit ? "Update" : "Add Diamond"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddDiamondModel;
